import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowDown, ArrowUpRight, Mail, Linkedin, Download } from "lucide-react";
import { CaseCard } from "./PathSection";
import MoreSubsection from "./MoreSubsections";
import { AIPortfolioShowcase, DesignPortfolioShowcase } from "./PortfolioShowcase";
import { PATHS, MORE_SUBSECTIONS, CLOSE, IMPACT_TILES } from "../data";

const ACCENT = {
  impact:     { hex: "#B5793F", gradient: "linear-gradient(180deg, #B5793F, #23C795)" },
  experience: { hex: "#B5793F", gradient: "linear-gradient(180deg, #B5793F, #B5793F)" },
  business:   { hex: "#23C795", gradient: "linear-gradient(180deg, #23C795, #23C795)" },
  ai:         { hex: "#19B5DE", gradient: "linear-gradient(180deg, #19B5DE, #19B5DE)" },
  design:     { hex: "#262016", gradient: "linear-gradient(180deg, #262016, #262016)" },
  education:  { hex: "#B5793F", gradient: "linear-gradient(180deg, #B5793F, #B5793F)" },
  skills:     { hex: "#23C795", gradient: "linear-gradient(180deg, #23C795, #23C795)" },
  leadership: { hex: "#19B5DE", gradient: "linear-gradient(180deg, #19B5DE, #19B5DE)" },
  exposure:   { hex: "#B5793F", gradient: "linear-gradient(180deg, #B5793F, #B5793F)" },
  more:       { hex: "#B5793F", gradient: "linear-gradient(180deg, #B5793F, #19B5DE, #23C795)" },
};
const ACCENT_NAME = { impact: "warm", experience: "wood", business: "mint", ai: "blue", design: "ink", education: "wood", skills: "mint", leadership: "blue", exposure: "wood", more: "warm" };

const STANDALONE_MORE_IDS = ["education", "skills", "leadership"];

// ---------------- Impact — Row 1 ----------------
const TILE_ACCENT = { wood: "#B5793F", mint: "#23C795", blue: "#19B5DE", ink: "#262016" };
const TILE_TINT = {
  wood: "linear-gradient(135deg, rgba(181,121,63,0.10), rgba(181,121,63,0.02))",
  mint: "linear-gradient(135deg, rgba(35,199,149,0.10), rgba(35,199,149,0.02))",
  blue: "linear-gradient(135deg, rgba(25,181,222,0.10), rgba(25,181,222,0.02))",
  ink:  "linear-gradient(135deg, rgba(38,32,22,0.08),  rgba(38,32,22,0.02))",
};
const TILE_SPAN = {
  small:   "md:col-span-3 lg:col-span-3",
  regular: "md:col-span-6 lg:col-span-4",
  wide:    "md:col-span-6 lg:col-span-6",
};

/** Render **bold** markers inside plain text */
function renderTileText(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#262016]">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

function ImpactTile({ tile, index }) {
  const accent = TILE_ACCENT[tile.accent] || TILE_ACCENT.wood;
  const isLink = Boolean(tile.href);
  const commonProps = {
    initial: { opacity: 0, y: 14 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.55, delay: 0.04 * index, ease: [0.22, 1, 0.36, 1] },
    className: `rp-foam relative overflow-hidden ${TILE_SPAN[tile.size] || TILE_SPAN.regular} col-span-1 flex flex-col ${isLink ? "cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`,
    style: {
      background: TILE_TINT[tile.accent] || TILE_TINT.wood,
      padding: "22px 24px",
      minHeight: tile.size === "small" ? 150 : 190,
    },
    "data-testid": `impact-tile-${tile.id}`,
  };
  const inner = (
    <>
      <span className="absolute top-0 left-6 h-1 w-10 rounded-b-full" style={{ background: accent }} />
      <p className="rp-eyebrow mb-2" style={{ color: accent }}>{tile.eyebrow}</p>
      <p
        className="rp-display font-medium leading-[1.1] mb-2"
        style={{
          fontSize: tile.size === "small" ? "clamp(1.6rem, 2.4vw, 2rem)" : "clamp(1.35rem, 1.9vw, 1.7rem)",
          textWrap: "balance",
          color: tile.size === "small" ? accent : "#262016",
        }}
      >
        {tile.big}
      </p>
      <p
        className="text-[13.5px] md:text-sm text-[#5C5042] leading-relaxed mt-1"
        style={{ textWrap: "pretty" }}
      >
        {renderTileText(tile.text)}
      </p>
    </>
  );
  if (isLink) {
    const isExternal = /^https?:\/\//.test(tile.href);
    return (
      <motion.a
        href={tile.href}
        {...(isExternal ? { target: "_blank", rel: "noreferrer noopener" } : {})}
        {...commonProps}
      >
        {inner}
      </motion.a>
    );
  }
  return <motion.article {...commonProps}>{inner}</motion.article>;
}

function ImpactContent() {
  return (
    <div className="px-6 md:px-10 lg:px-14 pt-6 md:pt-8 pb-8 md:pb-10">
      {/* Tile grid — full width */}
      <p className="rp-block-title">What matters most</p>
      <div
        className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-3 md:gap-4"
        data-testid="impact-tiles"
      >
        {IMPACT_TILES.map((tile, i) => (
          <ImpactTile key={tile.id} tile={tile} index={i} />
        ))}
      </div>
    </div>
  );
}

// ---------------- More (formerly Personality) ----------------
function MoreContent() {
  const [activeSub, setActiveSub] = useState(MORE_SUBSECTIONS[0].id);
  const active = MORE_SUBSECTIONS.find((s) => s.id === activeSub);

  return (
    <div className="px-6 md:px-10 lg:px-14 pt-6 md:pt-8 pb-8 md:pb-10">
      <p className="rp-display rp-italic text-base md:text-lg text-[#5C5042] max-w-2xl mb-10">
        Everything else that closes the loop.
      </p>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="more-subtabs">
        {MORE_SUBSECTIONS.map((s) => {
          const isActive = activeSub === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSub(s.id)}
              data-testid={`more-tab-${s.id}`}
              className="px-4 py-2 rounded-full text-sm font-medium transition-all"
              style={{
                background: isActive ? "#262016" : "transparent",
                color: isActive ? "#EDE7D9" : "#262016",
                border: isActive ? "1px solid #262016" : "1px solid rgba(38,32,22,0.18)",
              }}
            >
              {s.title}
            </button>
          );
        })}
      </div>

      {/* Sub-section content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="rp-foam p-6 md:p-10 lg:p-12"
          data-testid={`more-content-${active.id}`}
        >
          <MoreSubsection id={active.id} />
        </motion.div>
      </AnimatePresence>

      {/* Close — contact CTA */}
      <div className="mt-14 md:mt-20 text-center" data-testid="more-close">
        <ArrowDown className="mx-auto mb-6 text-[#B5793F]" size={18} />
        <p className="rp-display rp-italic font-medium text-2xl md:text-4xl text-[#262016] leading-[1.15] mb-8 max-w-3xl mx-auto"
           style={{ textWrap: "balance" }}>
          <span className="rp-underline">{CLOSE.line}</span>
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href={`mailto:${CLOSE.email}`}
             className="rp-btn rp-btn-primary" data-testid="contact-email-btn">
            <Mail size={15} /> {CLOSE.email}
          </a>
          <a href={CLOSE.linkedin}
             target="_blank" rel="noopener noreferrer"
             className="rp-btn rp-btn-ghost" data-testid="contact-linkedin-btn">
            <Linkedin size={15} /> LinkedIn
          </a>
          <a href={CLOSE.resume}
             className="rp-btn rp-btn-ghost" data-testid="contact-resume-btn">
            <Download size={15} /> Resume
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------------- Path content (paths 1-4) ----------------
function PathContent({ path, onNext, isLast }) {
  const accent = ACCENT[path.id]?.hex || "#262016";
  const isShowcase = path.kind === "ai-portfolio" || path.kind === "design-portfolio";
  const caseStudiesNode = (
    <div className="rp-foam bg-white/55 divide-y divide-[#26201618]">
      {path.cases?.map((c, i) => (
        <CaseCard key={i} content={c} delay={0.05 + i * 0.06}
                  accent={ACCENT_NAME[path.id] || "ink"} testId={`case-${path.id}-${i}`} />
      ))}
    </div>
  );
  return (
    <div className="px-6 md:px-10 lg:px-14 pt-6 md:pt-8 pb-8 md:pb-10">
      <p className="rp-display rp-italic text-base md:text-lg text-[#5C5042] mb-8 md:mb-10"
         style={{ textWrap: "pretty" }}>
        {path.intro}
      </p>
      {path.kind === "ai-portfolio" ? (
        <AIPortfolioShowcase caseStudiesNode={caseStudiesNode} />
      ) : path.kind === "design-portfolio" ? (
        <DesignPortfolioShowcase caseStudiesNode={caseStudiesNode} />
      ) : (
        caseStudiesNode
      )}

      {path.knowMore && (
        <div className="mt-10 flex justify-center">
          <a
            href={path.knowMore.href || "/design-portfolio"}
            data-testid={`${path.id}-know-more-btn`}
            className="group inline-flex items-center gap-3 rounded-full px-8 py-4 md:px-10 md:py-5 rp-display text-lg md:text-xl font-semibold tracking-tight text-[#F6F1E5] transition-transform hover:-translate-y-0.5 shadow-[0_18px_40px_-14px_rgba(38,32,22,0.55)]"
            style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}
          >
            <span>{path.knowMore.label || "View Full Portfolio"}</span>
            <ArrowUpRight
              size={22}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              style={{ color: "#EAB16A" }}
            />
          </a>
        </div>
      )}

      {!isLast && (
        <div className="mt-10 flex justify-center">
          <button onClick={onNext} data-testid={`accordion-next-${path.id}`}
                  className="inline-flex items-center gap-2 text-sm text-[#5C5042] hover:text-[#262016] transition-colors group">
            Open next
            <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform" style={{ color: accent }} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------- Row ----------------
function AccordionRow({ row, isOpen, onToggle, onNext, isLast }) {
  const accent = ACCENT[row.id];
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      const el = contentRef.current.closest("[data-accordion-row]");
      if (el) {
        // Scroll so the accordion BAR sits at the top of the viewport, so the
        // user sees the START of the section. Runs twice: once right away and
        // once after the open/close animations settle (another row collapsing
        // above shifts the layout and would leave the view at the bottom).
        const scrollToBar = () => {
          const rect = el.getBoundingClientRect();
          const targetY = rect.top + window.scrollY - 16;
          window.scrollTo({ top: targetY, behavior: "smooth" });
        };
        const t1 = setTimeout(scrollToBar, 80);
        const t2 = setTimeout(scrollToBar, 650);
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
    }
  }, [isOpen]);

  return (
    <div
      data-accordion-row
      id={`row-${row.id}`}
      className="border-b border-[#26201615] scroll-mt-4"
      data-testid={`accordion-row-${row.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`accordion-bar-${row.id}`}
        aria-expanded={isOpen}
        className="group w-full text-left px-6 md:px-10 lg:px-14 py-5 md:py-7 flex items-center gap-5 md:gap-8 relative overflow-hidden"
        style={{ background: isOpen ? "rgba(255,255,255,0.5)" : "transparent" }}
      >
        <span
          className="absolute left-0 top-0 bottom-0 w-1.5 transition-all duration-300"
          style={{
            background: accent.gradient,
            opacity: isOpen ? 1 : 0.45,
            transform: isOpen ? "scaleY(1)" : "scaleY(0.5)",
          }}
        />
        <h2 className="rp-display flex-1 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#262016] pl-2 md:pl-4"
            style={{ textWrap: "balance" }}>
          {row.label}
        </h2>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#26201620] flex items-center justify-center group-hover:border-[#262016] transition-colors"
          style={{ color: accent.hex }}
        >
          <Plus size={20} strokeWidth={1.6} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            ref={contentRef}
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
          >
            <motion.div
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
            >
              {row.id === "more" ? (
                <MoreContent />
              ) : row.id === "impact" ? (
                <ImpactContent />
              ) : STANDALONE_MORE_IDS.includes(row.id) ? (
                <div className="px-6 md:px-10 lg:px-14 pt-6 md:pt-8 pb-8 md:pb-10">
                  <MoreSubsection id={row.id} />
                </div>
              ) : (
                <PathContent path={row} onNext={onNext} isLast={isLast} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Accordion() {
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));
  const openNext = (currentId) => {
    const idx = PATHS.findIndex((p) => p.id === currentId);
    const next = PATHS[idx + 1];
    if (next) setOpenId(next.id);
  };

  // Deep-link handler:
  //   #path-<row-id>             → open that row
  //   #case-<row-id>-<index>     → open row + scroll to that case card
  //   #more-<subsection-id>      → open More row (or standalone) + activate subtab
  React.useEffect(() => {
    const handleHash = () => {
      const raw = (window.location.hash || "").replace(/^#/, "");
      if (!raw) return;

      let rowId = null;
      let caseIndex = null;
      let subId = null;

      const mCase = raw.match(/^case-([a-z]+)-(\d+)$/i);
      const mPath = raw.match(/^path-([a-z]+)$/i);
      const mMore = raw.match(/^more-([a-z]+)$/i);
      const mSub = raw.match(/^sub-([a-z]+)-([a-z-]+)$/i);

      if (mCase) { rowId = mCase[1]; caseIndex = Number(mCase[2]); }
      else if (mPath) { rowId = mPath[1]; }
      else if (mSub) { rowId = mSub[1]; }
      else if (mMore) {
        // Some subsections are standalone rows (education / leadership);
        // otherwise they live inside the "more" row.
        subId = mMore[1];
        const standalone = ["education", "leadership"];
        rowId = standalone.includes(subId) ? subId : "more";
      }

      if (!rowId) return;
      const validRow = PATHS.some((p) => p.id === rowId);
      if (!validRow) return;
      setOpenId(rowId);

      // Sub-section deep links (#sub-ai-images etc.) are scrolled/opened by
      // the SubAccordion itself once it mounts — skip the generic bar scroll.
      if (mSub) return;

      // Wait for the accordion body to render, then scroll.
      window.setTimeout(() => {
        let target = null;
        if (caseIndex !== null) {
          target = document.querySelector(`[data-testid="case-${rowId}-${caseIndex}"]`);
        }
        if (!target && subId) {
          const tab = document.querySelector(`[data-testid="more-tab-${subId}"]`);
          if (tab) tab.click();
          target = document.querySelector(`[data-testid="more-${subId}"]`);
        }
        if (!target) target = document.querySelector(`[data-testid="accordion-bar-${rowId}"]`);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    };

    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  return (
    <section className="rp-section pt-0 pb-0 relative" style={{ background: "#EDE7D9", zIndex: 5 }}
             data-testid="accordion-section">
      <div className="max-w-6xl mx-auto px-0 md:px-4 lg:px-6">
        <div className="border-t border-[#26201615]">
          {PATHS.map((row, i) => (
            <AccordionRow
              key={row.id}
              row={row}
              isOpen={openId === row.id}
              onToggle={() => toggle(row.id)}
              onNext={() => openNext(row.id)}
              isLast={i === PATHS.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
